import {
  IconBookmark,
  IconChevronDown,
  IconFolder,
  IconLoader2,
  IconLayoutList,
  IconLogout,
  IconLogin,
  IconMenu2,
  IconNews,
  IconPlus,
  IconSearch,
  IconSettings,
  IconShieldLock,
  IconUser,
  IconUsersPlus,
  IconX,
} from '@tabler/icons-react'
import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { SiteWordmark } from '@/components/branding/SiteWordmark'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/cn'
import { useAuth } from '@/lib/auth'
import { apiFetch } from '@/lib/api'
import type { ForumCategorySummary } from '@/lib/types'

type SvgIcon = ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' }>

type ShellNavItem = {
  label: string
  to: string
  icon: SvgIcon
  end?: boolean
}

type ShellNavGroup = {
  title: string
  items: ShellNavItem[]
}

function SkipLink() {
  return (
    <a
      href="#forum-main"
      className={cn(
        'absolute left-3 top-3 z-[100] -translate-y-[170%]',
        'rounded-md bg-white px-3 py-2 text-sm font-semibold text-zinc-950 shadow-md',
        'transition-transform focus-visible:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2',
      )}
    >
      Skip to main content
    </a>
  )
}

function DesktopFooter() {
  return (
    <footer className="shrink-0 border-t border-white/10 bg-zinc-950 py-6 text-[14px] text-zinc-400 sm:py-7">
      <div className="nk-container flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="max-w-lg">
          <Link
            to="/"
            className="inline-block rounded-sm no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
          >
            <SiteWordmark forDarkBg className="h-9 sm:h-10" loading="lazy" />
          </Link>
          <span className="sr-only">NK Our Town</span>

          <p className="mt-3 font-medium text-zinc-500">Neighbor forums for North Kingstown, Rhode Island.</p>
        </div>

        <p className="shrink-0 sm:pt-0.5 sm:text-right">
          <a
            className="font-medium text-zinc-200 no-underline transition hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
            href="https://websites-by-tim.com"
          >
            Designed by Websites by Tim
          </a>
        </p>
      </div>
    </footer>
  )
}

function SidebarCategoriesMobile({
  categories,
  catError,
  trending,
  onNavigate,
}: {
  categories: ForumCategorySummary[]
  catError: string | null
  trending: ForumCategorySummary[]
  onNavigate: () => void
}) {
  return (
    <div className="flex flex-col gap-5">
      {catError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] font-medium text-red-950">{catError}</div>
      ) : null}

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">Boards</p>
        <nav aria-label="Boards" className="flex flex-col gap-px">
          <NavLink
            end
            to="/discussions"
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'rounded-md px-2 py-2 text-sm font-medium no-underline',
                isActive ? 'bg-zinc-900 text-white' : 'text-zinc-700 hover:bg-zinc-50',
              )
            }
          >
            All threads
          </NavLink>
          {categories.map(c => (
            <NavLink
              key={c.id}
              to={`/c/${c.slug}`}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  'flex items-center justify-between gap-3 rounded-md px-2 py-2 text-sm font-medium no-underline',
                  isActive ? 'bg-sky-600 text-white' : 'text-zinc-700 hover:bg-zinc-50',
                )
              }
            >
              <span className="min-w-0 truncate">{c.name}</span>
              <span className="tabular-nums text-[11px] opacity-75">{c.threadCount ?? 0}</span>
            </NavLink>
          ))}
        </nav>
      </div>

      {trending.length > 0 ? (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">Trending boards</p>
          <ul className="flex flex-col gap-1">
            {trending.map(c => (
              <li key={`t-${c.id}`}>
                <Link className="block rounded-md px-2 py-1.5 text-sm text-zinc-700 hover:bg-zinc-50" to={`/c/${c.slug}`} onClick={onNavigate}>
                  {c.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <hr className="border-zinc-200" />

      <div className="text-[13px] leading-snug text-zinc-600">
        <Link className="font-semibold text-sky-700 no-underline" to="/guidelines" onClick={onNavigate}>
          Full guidelines →
        </Link>
      </div>
    </div>
  )
}

function RightRail() {
  const { auth } = useAuth()
  const cardWrap = 'overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm shadow-zinc-950/5'
  const cardTitle =
    'border-b border-zinc-100 bg-zinc-50 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-zinc-600'

  const participationText =
    auth.status === 'signedIn'
      ? 'You can start topics, reply, bookmark threads, and use the tools in the header on each thread.'
      : auth.status === 'loading'
        ? 'Checking your session…'
        : 'Sign in to start topics, reply, bookmark, and report issues when available.'

  return (
    <div className="flex w-full min-w-0 flex-col gap-4">
      <aside aria-labelledby="guidelines-mini" className={cardWrap}>
        <h2 id="guidelines-mini" className={cardTitle}>
          Guidelines
        </h2>
        <div className="px-4 py-4 text-[14px] leading-relaxed text-zinc-700">
          <p>Clear titles, real details (where, when, sources). Respect schools and minors.</p>
          <div className="mt-4 border-t border-zinc-100 pt-4">
            <Link className="text-sm font-semibold text-sky-700 no-underline hover:text-zinc-900" to="/guidelines">
              Read full guidelines →
            </Link>
          </div>
        </div>
      </aside>

      <aside aria-labelledby="about-mini" className={cardWrap}>
        <h2 id="about-mini" className={cardTitle}>
          Participation
        </h2>
        <p className="px-4 py-4 text-[14px] leading-relaxed text-zinc-700">{participationText}</p>
      </aside>
    </div>
  )
}

function deriveGroupIndex(pathname: string): number {
  const root = pathname.replace(/\/+$/, '') || '/'
  if (root === '/') return -1
  if (pathname.startsWith('/settings') || pathname.startsWith('/admin')) return 2
  if (
    pathname.startsWith('/new') ||
    pathname.startsWith('/bookmarks') ||
    pathname.startsWith('/guidelines')
  )
    return 1
  return 0
}

function useDismissOnOutsideClick(open: boolean, onClose: () => void, enabled = true) {
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open || !enabled) return
    function handler(e: MouseEvent) {
      const el = ref.current
      if (!el?.contains(e.target as Node)) onClose()
    }
    window.addEventListener('mousedown', handler)
    return () => window.removeEventListener('mousedown', handler)
  }, [enabled, open, onClose])

  return ref
}

function SubNavStrip({ items, suppressBottomDivider }: { items: ShellNavItem[]; suppressBottomDivider?: boolean }) {
  return (
    <div
      className={cn(
        'flex h-11 min-h-11 items-center gap-1 overflow-x-auto bg-zinc-50/90 px-3 md:px-5',
        suppressBottomDivider ? 'border-b-0' : 'border-b border-zinc-200',
      )}
    >
      {items.map(item => {
        const Icon = item.icon
        return (
          <NavLink
            key={`${item.to}-${item.label}`}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[13px] font-semibold no-underline whitespace-nowrap',
                isActive ? 'bg-white text-zinc-900 shadow-sm ring-1 ring-zinc-200' : 'text-zinc-600 hover:bg-white/70 hover:text-zinc-900',
              )
            }
          >
            <Icon className="size-4 shrink-0 opacity-80" aria-hidden />
            {item.label}
          </NavLink>
        )
      })}
    </div>
  )
}

export function ForumShell() {
  const [categories, setCategories] = useState<ForumCategorySummary[]>([])
  const [catError, setCatError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true

    void apiFetch<{ categories: ForumCategorySummary[] }>(`/api/categories`)
      .then(payload => {
        if (!alive) return
        setCategories(payload.categories ?? [])
        setCatError(null)
      })
      .catch(() => {
        if (!alive) return
        setCatError('Could not load boards. Refresh the page.')
      })

    return () => {
      alive = false
    }
  }, [])

  const trending = useMemo(
    () => [...categories].sort((a, b) => (b.threadCount ?? 0) - (a.threadCount ?? 0)).slice(0, 8),
    [categories],
  )

  const { auth, signOut } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  /** Normalize trailing slashes; treat only the index route as "home" for the top tab */
  const pathRoot = location.pathname.replace(/\/+$/, '') || '/'
  const isLandingHome = pathRoot === '/'
  const homeNavActive = pathRoot === '/'

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [activeGroupIndex, setActiveGroupIndex] = useState(() => deriveGroupIndex(location.pathname))

  const [qInput, setQInput] = useState(() => new URLSearchParams(location.search).get('q') ?? '')
  useEffect(() => setQInput(new URLSearchParams(location.search).get('q') ?? ''), [location.search])

  useEffect(() => {
    setActiveGroupIndex(deriveGroupIndex(location.pathname))
  }, [location.pathname])

  useEffect(() => {
    function onEsc(e: KeyboardEvent) {

      if (e.key === 'Escape') setMobileMenuOpen(false)
    }
    window.addEventListener('keydown', onEsc)
    return () => window.removeEventListener('keydown', onEsc)
  }, [])

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileMenuOpen])

  const authNext = useMemo(
    () => encodeURIComponent(`${location.pathname}${location.search}`),
    [location.pathname, location.search],

  )

  const participateItems: ShellNavItem[] = useMemo(
    () => [
      { label: 'New topic', to: '/new', icon: IconPlus },
      { label: 'Bookmarks', to: '/bookmarks', icon: IconBookmark },
      { label: 'Guidelines', to: '/guidelines', icon: IconNews },

    ],

    [],

  )

  const accountItems: ShellNavItem[] = useMemo(() => {
    if (auth.status === 'loading') {
      return [{ label: 'Settings', to: '/settings', icon: IconSettings }]
    }

    if (auth.status === 'anonymous') {
      return [
        { label: 'Sign in', to: `/sign-in?next=${authNext}`, icon: IconLogin },
        { label: 'Register', to: `/sign-up?next=${authNext}`, icon: IconUsersPlus },
        { label: 'Settings', to: '/settings', icon: IconSettings },
      ]
    }

    const items: ShellNavItem[] = [
      { label: 'Profile', to: `/u/${encodeURIComponent(auth.user.username)}`, icon: IconUser },
      { label: 'Settings', to: '/settings', icon: IconSettings },
    ]
    if (auth.user.role === 'moderator' || auth.user.role === 'admin') {
      items.push({ label: 'Moderation', to: '/admin', icon: IconShieldLock })
    }

    return items
  }, [auth, authNext])

  const forumItems: ShellNavItem[] = useMemo(() => {
    const all: ShellNavItem = {
      label: 'All threads',
      to: '/discussions',
      icon: IconLayoutList,

      end: true,
    }

    const boards: ShellNavItem[] = categories.map(c => ({
      label: c.name,
      to: `/c/${c.slug}`,
      icon: IconFolder,
    }))
    return [all, ...boards]

  }, [categories])

  const groupItemsFull: ShellNavGroup[] = useMemo(
    () => [
      { title: 'Forum', items: forumItems },
      { title: 'Participate', items: participateItems },
      { title: 'Account', items: accountItems },
    ],
    [forumItems, participateItems, accountItems],

  )

  const activeGroupItems =
    activeGroupIndex < 0
      ? []
      : groupItemsFull[Math.min(activeGroupIndex, groupItemsFull.length - 1)]!.items

  const showSubNavStrip = !isLandingHome || activeGroupIndex >= 0

  return (
    <div
      className={cn(
        'flex min-h-0 flex-1 flex-col overflow-x-clip bg-zinc-100',
        isLandingHome &&
          'bg-[radial-gradient(ellipse_120%_90%_at_50%_-15%,rgba(14,165,233,0.11),transparent_50%),linear-gradient(180deg,#fafafa_0%,#f4f4f5_100%)]',
      )}
    >

      <SkipLink />

      <header className="sticky top-0 z-50 shrink-0 bg-white">

        {/* Row 1: ApplicationShell-style top rail */}
        <div className="flex h-14 items-center gap-3 border-b border-zinc-200 px-3 md:px-5 lg:gap-6">
          <button
            type="button"

            className="-ml-0.5 lg:hidden"

            aria-label="Open navigation"
            aria-expanded={mobileMenuOpen}
            aria-controls="forum-mobile-nav"
            id="forum-open-mobile"
            onClick={() => setMobileMenuOpen(open => !open)}
          >
            <Button variant="outline" size="icon" className="border-zinc-200 bg-white hover:bg-zinc-50">

              <IconMenu2 className="size-5 text-zinc-800" aria-hidden />
            </Button>
          </button>

          <Link
            to="/"
            className="flex min-w-0 max-w-[min(100%,13rem)] shrink-0 items-center gap-2 no-underline sm:max-w-[15rem] md:max-w-[18rem]"
          >
            <SiteWordmark className="h-7 sm:h-8 md:h-9" />
            <span className="sr-only">NK Our Town — home</span>
            <span className="hidden truncate text-[11px] font-medium uppercase tracking-wider text-zinc-500 lg:inline">
              Forums
            </span>
          </Link>

          <nav className="ml-2 hidden shrink-0 items-center gap-0.5 md:ml-4 md:flex" aria-label="Primary navigation">

            <Link
              to="/"
              aria-current={homeNavActive ? 'page' : undefined}
              className={cn(
                'inline-flex h-9 items-center justify-center whitespace-nowrap border-2 border-transparent bg-white px-3 text-[13px] font-semibold text-zinc-700 no-underline shadow-none transition-colors',
                'rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nk-sea focus-visible:ring-offset-2 focus-visible:ring-offset-white',
                homeNavActive ? 'bg-zinc-100 text-zinc-950' : 'hover:bg-zinc-50',
              )}
            >
              Home
            </Link>

            {[0, 1, 2].map(ix => (
              <Button
                key={ix}

                variant="outline"
                size="sm"
                type="button"
                aria-pressed={activeGroupIndex === ix}
                onClick={() => setActiveGroupIndex(ix)}
                className={cn(
                  'rounded-lg border-transparent font-semibold text-zinc-700 shadow-none',

                  activeGroupIndex >= 0 && activeGroupIndex === ix ? 'bg-zinc-100 text-zinc-950' : 'hover:bg-zinc-50',
                )}
              >
                {groupItemsFull[ix]!.title}
              </Button>
            ))}
          </nav>

          <div className="ml-auto flex min-w-0 items-center gap-2">
            <form
              className="relative hidden min-w-[12rem] max-w-[20rem] flex-1 md:block"
              role="search"
              onSubmit={evt => {
                evt.preventDefault()
                const qp = qInput.trim() ? `?q=${encodeURIComponent(qInput.trim())}` : ''

                navigate(`/discussions${qp}`)
                setMobileMenuOpen(false)
              }}
            >
              <IconSearch className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-zinc-400" aria-hidden />

              <label className="sr-only" htmlFor="forum-search-desktop">
                Search topics
              </label>
              <input
                id="forum-search-desktop"
                name="forum-search-desktop"
                value={qInput}
                onChange={evt => setQInput(evt.target.value)}
                placeholder="Search…"
                className="h-9 w-full rounded-md border border-zinc-200 bg-white pr-3 pl-9 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/30"
              />
            </form>

            {auth.status === 'loading' ? (
              <div className="flex shrink-0 items-center gap-2" aria-busy="true" aria-label="Checking session">
                <Button variant="outline" size="sm" disabled className="rounded-lg border-zinc-200 font-semibold opacity-80">
                  <IconLoader2 className="size-4 animate-spin" aria-hidden="true" />
                  <span className="sr-only">Checking session</span>
                </Button>
              </div>
            ) : auth.status === 'signedIn' ? (
              <div className="flex shrink-0 items-center gap-2">
                <UserTray
                  avatarUrl={auth.user.avatarUrl}
                  displayName={auth.user.displayName}
                  username={auth.user.username}
                  email={auth.user.email}
                  role={auth.user.role}
                  onSignOut={() => {
                    void signOut()
                  }}
                />

                <Link to="/new" className="no-underline lg:hidden">
                  <Button size="sm" className="rounded-lg bg-sky-600 font-semibold text-white hover:bg-sky-500">
                    Post
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="flex shrink-0 items-center gap-2">
                <Link className="no-underline" to={`/sign-in?next=${encodeURIComponent(location.pathname + location.search)}`}>
                  <Button variant="outline" size="sm" className="rounded-lg border-zinc-200 font-semibold">
                    Sign in
                  </Button>
                </Link>
                <Link className="no-underline hidden sm:inline" to={`/sign-up?next=${encodeURIComponent(location.pathname + location.search)}`}>
                  <Button size="sm" className="rounded-lg bg-sky-600 font-semibold text-white hover:bg-sky-500">
                    Register
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Row 2: Sub-navigation (hidden on landing until a group tab is chosen) */}
        {showSubNavStrip ? (
          <SubNavStrip items={activeGroupItems} suppressBottomDivider={isLandingHome} />
        ) : null}

        {/* Mobile search (narrow screens: avoid duplicate IDs with desktop field) */}
        <form

          role="search"
          className={cn(
            'flex gap-2 bg-white px-3 py-2 md:hidden',
            isLandingHome ? 'border-b-0' : 'border-b border-zinc-200',
          )}

          onSubmit={evt => {
            evt.preventDefault()

            const qp = qInput.trim() ? `?q=${encodeURIComponent(qInput.trim())}` : ''

            navigate(`/discussions${qp}`)
          }}
        >

          <label className="sr-only" htmlFor="forum-search-mobile">
            Search topics
          </label>
          <div className="relative min-w-0 flex-1">
            <IconSearch className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-zinc-400" aria-hidden />
            <input

              id="forum-search-mobile"
              value={qInput}
              onChange={evt => setQInput(evt.target.value)}
              placeholder="Search threads…"

              className="h-9 w-full rounded-md border border-zinc-200 bg-zinc-50/80 pr-3 pl-9 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-sky-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-sky-500/30"
            />
          </div>
          <Button type="submit" variant="outline" size="sm" className="h-9 shrink-0 rounded-md border-zinc-200 font-semibold">
            Search
          </Button>
        </form>
      </header>

      {mobileMenuOpen ? (
        <div className="fixed inset-0 z-[60] lg:hidden" role="presentation">
          <button
            type="button"

            className="absolute inset-0 bg-zinc-950/40"

            aria-label="Close navigation"
            tabIndex={-1}
            onClick={() => setMobileMenuOpen(false)}
          />

          <div
            id="forum-mobile-nav"
            className="absolute left-0 top-0 flex h-[100svh] w-[min(20rem,calc(100vw-4rem))] flex-col gap-6 overflow-y-auto border-r border-zinc-200 bg-white p-5 shadow-xl"
          >
            <div className="flex items-start justify-between gap-3">
              <Link
                to="/"
                className="min-w-0 flex-1 rounded-sm no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                <SiteWordmark className="h-7 max-w-[11rem]" />
                <span className="sr-only">NK Our Town — home</span>
              </Link>
              <Button variant="outline" size="icon" className="shrink-0 border-zinc-200" onClick={() => setMobileMenuOpen(false)}>
                <IconX className="size-[1.125rem]" aria-hidden />
              </Button>
            </div>

            {groupItemsFull.map((g, ix) => (
              <div key={g.title}>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">{g.title}</p>

                <div className="flex flex-col gap-1">
                  {g.items.map(item => (
                    <NavLink
                      key={`${item.to}-${item.label}`}
                      to={item.to}
                      end={item.end}
                      onClick={() => {
                        setMobileMenuOpen(false)
                        setActiveGroupIndex(ix)
                      }}
                      className={({ isActive }) =>
                        cn(
                          'rounded-md px-2 py-2 text-sm font-medium no-underline',
                          isActive ? 'bg-zinc-900 text-white' : 'text-zinc-800 hover:bg-zinc-50',
                        )
                      }
                    >
                      {item.label}

                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
            <hr className="border-zinc-200" />

            <SidebarCategoriesMobile
              categories={categories}
              catError={catError}

              trending={trending}
              onNavigate={() => setMobileMenuOpen(false)}
            />
          </div>
        </div>
      ) : null}

      <div
        className={cn(
          'nk-container flex min-h-0 flex-1 flex-col gap-8 lg:flex-row lg:gap-10 lg:pb-10',
          isLandingHome ? 'pb-6 pt-0 lg:pb-10' : 'py-6 lg:pt-8',
        )}
      >

        <main id="forum-main" className="min-h-0 w-full min-w-0 flex-1">
          <div className={cn('w-full min-w-0', isLandingHome && 'mx-auto xl:max-w-[1180px]')}>

            <Outlet />
          </div>
        </main>

        {isLandingHome ? null : (
          <aside className="hidden w-full shrink-0 lg:block lg:w-[min(17.75rem,20vw)] xl:w-[19rem]" aria-label="Forum reference">
            <div className="lg:sticky lg:top-36">
              <RightRail />
            </div>
          </aside>
        )}
      </div>

      <DesktopFooter />
    </div>
  )
}

/** Separate from earlier mistaken UserMenuTray prototype with invalid slot JSX */
function UserTray({
  avatarUrl,
  displayName,
  username,

  email,

  role,

  onSignOut,
}: {
  avatarUrl: string | null
  displayName: string
  username: string

  email: string
  role: string
  onSignOut: () => void
}) {
  const [open, setOpen] = useState(false)

  const closeMenu = useCallback(() => setOpen(false), [])
  const ref = useDismissOnOutsideClick(open, closeMenu)
  const initial = displayName.trim().slice(0, 1).toUpperCase()
  const canModerate = role === 'moderator' || role === 'admin'

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        aria-expanded={open}

        aria-haspopup="menu"
        aria-controls="forum-user-menu"

        id="forum-user-trigger"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-2 py-1 shadow-sm hover:bg-zinc-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2"
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="size-8 shrink-0 rounded-full object-cover" loading="lazy" />

        ) : (
          <span className="grid size-8 shrink-0 place-items-center rounded-full bg-zinc-200 text-[13px] font-bold text-zinc-900" aria-hidden>
            {initial}
          </span>
        )}
        <span className="hidden max-w-[10rem] truncate text-[13px] font-semibold text-zinc-900 md:inline">{displayName}</span>
        <IconChevronDown className={cn('size-4 shrink-0 text-zinc-500 transition', open && 'rotate-180')} aria-hidden />

      </button>

      {open ? (
        <div
          id="forum-user-menu"
          role="menu"
          aria-labelledby="forum-user-trigger"
          className="absolute right-0 top-full z-50 mt-2 w-[16rem] overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-lg shadow-zinc-950/15 ring-1 ring-zinc-950/5"
        >
          <div className="border-b border-zinc-100 px-3 py-2.5">
            <p className="truncate text-sm font-semibold text-zinc-900">{displayName}</p>
            <p className="truncate text-xs font-medium text-zinc-500">@{username}</p>

            <p className="mt-1 truncate text-xs text-zinc-400">{email}</p>
          </div>
          <div className="p-1.5">
            <Link role="menuitem" className="block rounded-md px-2 py-2 text-[13px] font-medium text-zinc-800 no-underline hover:bg-zinc-50" to={`/u/${encodeURIComponent(username)}`} onClick={() => setOpen(false)}>
              <span className="inline-flex items-center gap-2">

                <IconUser className="size-4" aria-hidden />

                Profile

              </span>
            </Link>
            <Link role="menuitem" className="block rounded-md px-2 py-2 text-[13px] font-medium text-zinc-800 no-underline hover:bg-zinc-50" to="/settings" onClick={() => setOpen(false)}>
              <span className="inline-flex items-center gap-2">
                <IconSettings className="size-4" aria-hidden />

                Settings

              </span>

            </Link>
            {canModerate ? (
              <Link role="menuitem" className="block rounded-md px-2 py-2 text-[13px] font-medium text-zinc-800 no-underline hover:bg-zinc-50" to="/admin" onClick={() => setOpen(false)}>
                <span className="inline-flex items-center gap-2">
                  <IconShieldLock className="size-4" aria-hidden />
                  Moderation
                </span>
              </Link>
            ) : null}
          </div>
          <hr className="border-zinc-100" />

          <div className="p-1.5">

            <button
              role="menuitem"

              type="button"
              className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-[13px] font-semibold text-red-700 hover:bg-red-50"
              onClick={() => {

                setOpen(false)
                onSignOut()
              }}

            >
              <IconLogout className="size-4" aria-hidden />

              Sign out
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
